'use client'

import { useState } from 'react'
import { motion } from 'framer-motion'
import { useForm, Controller } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { supportFormSchema } from "@/lib/validations"
import { Button } from '@/components/ui/button'
import { GradientButton } from '@/components/ui/gradient-button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import {
  ArrowLeft,
  Wrench,
  Download,
  Phone,
  Mail,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  FileText,
  Book,
  Video,
  Code,
  Zap,
  ArrowRight,
  MessageSquare,
} from 'lucide-react'
import { Breadcrumb } from '@/components/breadcrumb'
import { useRecaptcha } from '@/hooks/use-recaptcha'
import Link from 'next/link'
import { toast } from 'sonner'

const troubleshootingGuides = [
  {
    icon: AlertCircle,
    title: 'Component Not Functioning',
    category: 'Hardware Issues',
    description: 'Steps to diagnose and resolve component functionality problems',
    solutions: [
      'Verify power supply voltage and current ratings',
      'Check pin connections and solder joints',
      'Measure component values with multimeter',
      'Review datasheet specifications and operating conditions',
      'Test with known-good replacement component',
    ],
  },
  {
    icon: Zap,
    title: 'Intermittent Operation',
    category: 'Reliability',
    description: 'Troubleshoot components with inconsistent performance',
    solutions: [
      'Check for loose connections or cold solder joints',
      'Verify operating temperature is within specifications',
      'Test for electromagnetic interference (EMI)',
      'Inspect PCB for shorts or opens',
      'Review thermal management and heat dissipation',
    ],
  },
  {
    icon: Code,
    title: 'Integration Issues',
    category: 'Software/Firmware',
    description: 'Resolve problems integrating components into your system',
    solutions: [
      'Verify communication protocol settings (I2C, SPI, UART)',
      'Check pull-up/pull-down resistor requirements',
      'Review timing diagrams and clock frequencies',
      'Validate firmware initialization sequence',
      'Use logic analyzer to debug signal integrity',
    ],
  },
  {
    icon: FileText,
    title: 'Specification Clarification',
    category: 'Documentation',
    description: 'Understanding technical specifications and datasheets',
    solutions: [
      'Review latest datasheet revision from manufacturer',
      'Check application notes for use case examples',
      'Compare specifications across similar components',
      'Contact technical support for clarification',
      'Request design review from field application engineer',
    ],
  },
]

const documentTypes = [
  {
    icon: FileText,
    title: 'Datasheets',
    description: 'Complete technical specifications',
    count: '2.7M+',
    color: 'bg-blue-500',
  },
  {
    icon: Book,
    title: 'Application Notes',
    description: 'Implementation guides and examples',
    count: '15K+',
    color: 'bg-purple-500',
  },
  {
    icon: Code,
    title: 'Reference Designs',
    description: 'Circuit schematics and PCB layouts',
    count: '5K+',
    color: 'bg-green-500',
  },
  {
    icon: Video,
    title: 'Video Tutorials',
    description: 'Step-by-step video guides',
    count: '500+',
    color: 'bg-red-500',
  },
]

const supportTeam = [
  {
    name: 'Hardware Support',
    specialty: 'Circuit Design & Component Selection',
    responseTime: '< 1 hour',
    availability: '24/7',
    contact: 'hardware@prosoftelectronics.com',
  },
  {
    name: 'Software Support',
    specialty: 'Firmware, Drivers & Integration',
    responseTime: '< 2 hours',
    availability: '24/7',
    contact: 'software@prosoftelectronics.com',
  },
  {
    name: 'Application Engineering',
    specialty: 'Custom Solutions & Design Review',
    responseTime: '< 4 hours',
    availability: 'Mon-Fri 8AM-6PM EST',
    contact: 'applications@prosoftelectronics.com',
  },
]

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
    },
  },
}

type SupportFormData = z.infer<typeof supportFormSchema>

export default function TechnicalSupportPage() {
  const { register, handleSubmit, formState: { errors }, reset, control } = useForm<SupportFormData>({
    resolver: zodResolver(supportFormSchema),
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const { getToken } = useRecaptcha()

  const onSubmit = async (data: SupportFormData) => {
    setIsSubmitting(true)
    try {
      const recaptchaToken = await getToken('support_submit')
      if (!recaptchaToken && process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY) {
        toast.error('reCAPTCHA challenge failed. Please try again.')
        setIsSubmitting(false)
        return
      }

      const res = await fetch('/api/forms/support', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, recaptchaToken })
      })
      const result = await res.json()
      if (!res.ok) throw new Error(result?.error || 'Failed to submit support request')

      toast.success('Support request submitted! Our team will contact you shortly.')
      reset()
    } catch (err: any) {
      console.error('Support submit error', err)
      toast.error(err?.message || 'Failed to submit support request')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Breadcrumb
        items={[
          { href: '/', label: 'Home' },
          { href: '/support', label: 'Support' },
          { label: 'Technical Support', current: true },
        ]}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#518BC4] to-[#7C1414] text-white py-16">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              href="/support"
              className="inline-flex items-center text-white/80 hover:text-white transition-colors mb-6"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Support Center
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <Wrench className="h-8 w-8" />
              </div>
              <Badge variant="secondary" className="bg-white/10 text-white border-white/20">
                Expert Technical Assistance
              </Badge>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Technical Support</h1>
            <p className="text-xl text-white/90 max-w-3xl">
              Get expert help with component selection, circuit design, troubleshooting, and integration. Our
              experienced engineers are ready to assist you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Support Request Form */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-12">
              <Badge className="bg-[#518BC4]/10 text-[#518BC4] border-[#518BC4]/20 mb-4">
                Get Expert Help
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Submit Support Request</h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Fill out the form below and our technical team will respond based on the urgency level
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <Card className="border border-slate-200 shadow-lg">
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          {...register("name")}
                          placeholder="John Doe"
                          className="h-12"
                        />
                        {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          type="email"
                          {...register("email")}
                          placeholder="john.doe@company.com"
                          className="h-12"
                        />
                        {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="company">Company Name</Label>
                        <Input
                          id="company"
                          {...register("company")}
                          placeholder="Your Company"
                          className="h-12"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number</Label>
                        <Input
                          id="phone"
                          type="tel"
                          {...register("phone")}
                          placeholder="+1 (555) 000-0000"
                          className="h-12"
                        />
                        {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone.message}</p>}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="partNumber">Part Number</Label>
                        <Input
                          id="partNumber"
                          {...register("partNumber")}
                          placeholder="STM32F407VGT6"
                          className="h-12"
                        />
                        {errors.partNumber && <p className="text-red-500 text-xs mt-1">{errors.partNumber.message}</p>}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="urgency">Urgency Level *</Label>
                        <Controller
                          name="urgency"
                          control={control}
                          render={({ field }) => (
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <SelectTrigger id="urgency" className="h-12">
                                <SelectValue placeholder="Select urgency" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="critical">🔴 Critical - Production Down (&lt;1 hour)</SelectItem>
                                <SelectItem value="high">🟠 High - Urgent Issue (&lt;2 hours)</SelectItem>
                                <SelectItem value="medium">🟡 Medium - Important (&lt;4 hours)</SelectItem>
                                <SelectItem value="low">🟢 Low - General Question (&lt;24 hours)</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        />
                        {errors.urgency && <p className="text-red-500 text-xs mt-1">{errors.urgency.message}</p>}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="issueDescription">Issue Description *</Label>
                      <Textarea
                        id="issueDescription"
                        {...register("issueDescription")}
                        placeholder="Please describe your technical issue in detail. Include:&#10;- Component part number&#10;- Current behavior vs. expected behavior&#10;- Testing environment and conditions&#10;- Any error messages or symptoms&#10;- Steps already taken to troubleshoot"
                        rows={8}
                        className="resize-none"
                      />
                      {errors.issueDescription && <p className="text-red-500 text-xs mt-1">{errors.issueDescription.message}</p>}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4">
                      <GradientButton type="submit" size="lg" variant="blue-red" className="flex-1 group" disabled={isSubmitting}>
                        {isSubmitting ? "Submitting..." : "Submit Support Request"}
                        <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                      </GradientButton>
                      <Button
                        type="button"
                        variant="outline"
                        size="lg"
                        className="flex-1 border-slate-300 hover:border-[#518BC4] hover:text-[#518BC4]"
                      >
                        Save as Draft
                      </Button>
                    </div>

                    <p className="text-sm text-slate-500 text-center">
                      * Required fields. By submitting, you agree to our{' '}
                      <Link href="/legal/privacy" className="text-[#518BC4] hover:underline">
                        Privacy Policy
                      </Link>
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Troubleshooting Guides */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-6">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.div variants={itemVariants} className="text-center mb-12">
              <Badge className="bg-[#7C1414]/10 text-[#7C1414] border-[#7C1414]/20 mb-4">
                Self-Service Solutions
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Common Technical Issues
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Quick solutions to frequently encountered technical problems
              </p>
            </motion.div>

            <div className="grid lg:grid-cols-2 gap-6">
              {troubleshootingGuides.map((guide, index) => {
                const IconComponent = guide.icon
                return (
                  <motion.div key={index} variants={itemVariants}>
                    <Card className="h-full border border-slate-200 shadow-sm hover:shadow-lg transition-all">
                      <CardHeader className="pb-4">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-[#518BC4]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                            <IconComponent className="h-6 w-6 text-[#518BC4]" />
                          </div>
                          <div className="flex-1">
                            <Badge variant="outline" className="border-slate-300 text-slate-600 mb-2">
                              {guide.category}
                            </Badge>
                            <CardTitle className="text-xl text-slate-900">{guide.title}</CardTitle>
                            <p className="text-slate-600 mt-2">{guide.description}</p>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {guide.solutions.map((solution, solutionIndex) => (
                            <div key={solutionIndex} className="flex items-start gap-3">
                              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                              <span className="text-slate-600 text-sm">{solution}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                )
              })}
            </div>
          </motion.div>
        </div>
      </section>



      {/* Expected Response Times */}
      <section className="py-12 bg-slate-50">
        <div className="container mx-auto px-6">
          <Card className="border-0 shadow-xl bg-gradient-to-r from-slate-50 to-slate-100">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Expected Response Times</h3>
                <p className="text-slate-600">Based on issue urgency level</p>
              </div>

              <div className="grid md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
                    <div className="w-6 h-6 rounded-full bg-red-500"></div>
                  </div>
                  <div className="font-bold text-slate-900 mb-1">Critical</div>
                  <div className="text-2xl font-bold text-red-600 mb-1">&lt; 1 Hour</div>
                  <div className="text-sm text-slate-600">Production issues</div>
                </div>

                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-3">
                    <div className="w-6 h-6 rounded-full bg-orange-500"></div>
                  </div>
                  <div className="font-bold text-slate-900 mb-1">High</div>
                  <div className="text-2xl font-bold text-orange-600 mb-1">&lt; 2 Hours</div>
                  <div className="text-sm text-slate-600">Urgent problems</div>
                </div>

                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-3">
                    <div className="w-6 h-6 rounded-full bg-yellow-500"></div>
                  </div>
                  <div className="font-bold text-slate-900 mb-1">Medium</div>
                  <div className="text-2xl font-bold text-yellow-600 mb-1">&lt; 4 Hours</div>
                  <div className="text-sm text-slate-600">Important issues</div>
                </div>

                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                    <div className="w-6 h-6 rounded-full bg-green-500"></div>
                  </div>
                  <div className="font-bold text-slate-900 mb-1">Low</div>
                  <div className="text-2xl font-bold text-green-600 mb-1">&lt; 24 Hours</div>
                  <div className="text-sm text-slate-600">General questions</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Need a Technical Consultation?</h2>
            <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
              Schedule a call with our field application engineers for in-depth technical discussions and design
              reviews
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <GradientButton size="lg" variant="blue-red" className="group">
                <Calendar className="mr-2 h-5 w-5" />
                Schedule Consultation
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </GradientButton>
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-slate-900 bg-transparent"
              >
                <MessageSquare className="mr-2 h-5 w-5" />
                Start Live Chat
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}
